const FALLBACK_PUBLIC_BASE = "https://outpost.miulustek.com";

function resolveHttpUrl(value: string | undefined, fallback: string): string {
  const raw = value?.trim();
  if (!raw) return fallback;

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(raw)) {
    return raw;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return new URL(raw, window.location.origin).toString();
  }

  return new URL(raw, FALLBACK_PUBLIC_BASE).toString();
}

function resolveWsUrl(value: string | undefined, fallback: string): string {
  const raw = value?.trim();
  if (!raw) return fallback;

  if (/^wss?:\/\//i.test(raw)) {
    return raw;
  }

  if (/^https?:\/\//i.test(raw)) {
    const normalized = new URL(raw);
    normalized.protocol = normalized.protocol === "https:" ? "wss:" : "ws:";
    return normalized.toString();
  }

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : FALLBACK_PUBLIC_BASE;

  const normalized = new URL(raw, origin);
  normalized.protocol = normalized.protocol === "https:" ? "wss:" : "ws:";
  return normalized.toString();
}

const DEFAULT_API_URL = resolveHttpUrl(
  (import.meta.env.DEV ? import.meta.env.VITE_DEV_API_URL : undefined) ??
    (import.meta.env.VITE_API_URL as string),
  `${FALLBACK_PUBLIC_BASE}/api`,
);

export default {
  /**
   * Whether to emit additional debug information
   */
  DEBUG: import.meta.env.DEV || true,
  /**
   * What API server to connect to by default.
   */
  DEFAULT_API_URL,
  /**
   * Whether this points to the upstream public network.
   * Outpost deployments should evaluate false to hide upstream-specific CTAs.
   */
  IS_STOAT: [
    // historically...
    "https://api.revolt.chat",
    "https://beta.revolt.chat/api",
    "https://revolt.chat/api",
    // ... and now:
    "https://stoat.chat/api",
  ].includes(DEFAULT_API_URL),
  /**
   * What WS server to connect to by default.
   */
  DEFAULT_WS_URL: resolveWsUrl(
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_WS_URL : undefined) ??
      (import.meta.env.VITE_WS_URL as string),
    "wss://outpost.miulustek.com/ws",
  ),
  /**
   * What media server to connect to by default.
   */
  DEFAULT_MEDIA_URL: resolveHttpUrl(
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_MEDIA_URL : undefined) ??
      (import.meta.env.VITE_MEDIA_URL as string),
    "https://outpost.miulustek.com/autumn",
  ),
  /**
   * What proxy server to connect to by default.
   */
  DEFAULT_PROXY_URL: resolveHttpUrl(
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_PROXY_URL : undefined) ??
      (import.meta.env.VITE_PROXY_URL as string),
    "https://outpost.miulustek.com/january",
  ),
  /**
   * Optional URL for switching to a legacy web client.
   */
  LEGACY_WEB_URL: resolveHttpUrl(
    import.meta.env.VITE_LEGACY_WEB_URL as string,
    "https://outpost.miulustek.com/legacy",
  ),
  /**
   * Base URL for admin panel deep links.
   */
  ADMIN_PANEL_URL: resolveHttpUrl(
    import.meta.env.VITE_ADMIN_PANEL_URL as string,
    "https://outpost.miulustek.com",
  ),
  /**
   * Endpoint used for age-gate geoblocking checks.
   */
  GEO_URL: resolveHttpUrl(
    import.meta.env.VITE_GEO_URL as string,
    "https://outpost.miulustek.com/api/geo",
  ),
  /**
   * Base URL for unicode emoji assets.
   */
  UNICODE_EMOJI_URL: resolveHttpUrl(
    import.meta.env.VITE_UNICODE_EMOJI_URL as string,
    "https://outpost.miulustek.com/emoji",
  ),
  /**
   * hCaptcha site key to use if enabled
   */
  HCAPTCHA_SITEKEY: import.meta.env.VITE_HCAPTCHA_SITEKEY as string,
  /**
   * Maximum number of replies a message can have
   */
  MAX_REPLIES: (import.meta.env.VITE_CFG_MAX_REPLIES as number) ?? 5,
  /**
   * Maximum number of attachments a message can have
   */
  MAX_ATTACHMENTS: (import.meta.env.VITE_CFG_MAX_ATTACHMENTS as number) ?? 5,
  /**
   * Maximum number of emoji a server can have
   */
  MAX_EMOJI: (import.meta.env.VITE_CFG_MAX_EMOJI as number) ?? 100,
  /**
   * Max file size allowed for uploads (in bytes)
   * 20 MB = 20 * 1024 * 1024 = 20,971,520 bytes
   * I kinda wonder if this should be a setting, or something fetched from the backend dynamically.
   */
  MAX_FILE_SIZE:
    (import.meta.env.VITE_CFG_MAX_FILE_SIZE as number) ?? 20_000_000,
  /**
   * Session ID to set during development.
   */
  DEVELOPMENT_SESSION_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_SESSION_ID as string)
    : undefined,
  /**
   * Token to set during development.
   */
  DEVELOPMENT_TOKEN: import.meta.env.DEV
    ? (import.meta.env.VITE_TOKEN as string)
    : undefined,
  /**
   * User ID to set during development.
   */
  DEVELOPMENT_USER_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_USER_ID as string)
    : undefined,
};
